import React, { useCallback, useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import EventRow from "@/components/EventRow";
import LiveEventPreview from "@/components/LiveEventPreview";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Play, Info, Search, MapPin, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { VISIBLE_EVENT_CATEGORIES, filterVisibleEvents } from "@/config/eventCategories";

// US States for dropdown
const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "Washington D.C." }
];

const Home = ({ user, onLogout }) => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [featuredEventIndex, setFeaturedEventIndex] = useState(0);
  const [searchCity, setSearchCity] = useState("");
  const [searchState, setSearchState] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [isFeaturedPreviewActive, setIsFeaturedPreviewActive] = useState(false);
  const navigate = useNavigate();

  const handleFeaturedPreviewActiveChange = useCallback((active) => {
    setIsFeaturedPreviewActive(active);
  }, []);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    // Auto-cycle through featured events every 5 seconds
    const displayEvents = filteredEvents.length > 0 ? filteredEvents : events;
    if (displayEvents.length > 0 && !isFeaturedPreviewActive) {
      const interval = setInterval(() => {
        setFeaturedEventIndex((prevIndex) => (prevIndex + 1) % Math.min(displayEvents.length, 10));
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [events, filteredEvents, isFeaturedPreviewActive]);

  const fetchEvents = async () => {
    try {
      const response = await axiosInstance.get("/events");
      setEvents(filterVisibleEvents(response.data));
      setFilteredEvents([]);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const searchByLocation = async () => {
    if (!searchCity && !searchState) {
      // If no search criteria, reset to show all events
      setFilteredEvents([]);
      setFeaturedEventIndex(0);
      return;
    }

    setIsSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchCity) params.append("city", searchCity);
      if (searchState) params.append("state", searchState);
      
      const response = await axiosInstance.get(`/events/search/location?${params.toString()}`);
      // Backend returns {events: [...], count: ..., filters: {...}}
      setFilteredEvents(filterVisibleEvents(response.data.events));
      setFeaturedEventIndex(0);
    } catch (error) {
      console.error("Error searching events:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchCity("");
    setSearchState("");
    setSearchQuery("");
    setFilteredEvents([]);
    setFeaturedEventIndex(0);
    setShowSearchPanel(false);
  };

  // Base list: location-filtered results if a location (geo) search is active, else all
  const baseEvents = filteredEvents.length > 0 ? filteredEvents : events;

  // Keyword search (client-side) across title, category, venue, description, city/state
  const query = searchQuery.trim().toLowerCase();
  const displayEvents = query
    ? baseEvents.filter((event) =>
        `${event.title} ${event.category} ${event.venue || ""} ${event.description || ""} ${event.city || ""} ${event.state || ""}`
          .toLowerCase()
          .includes(query)
      )
    : baseEvents;

  // A search is active if there's a keyword OR a location filter
  const searchActive = query.length > 0 || filteredEvents.length > 0;

  const getEventsByCategory = (category) => {
    return displayEvents.filter((event) => event.category.toLowerCase() === category.toLowerCase());
  };

  const categories = VISIBLE_EVENT_CATEGORIES;
  const featuredEvent = displayEvents[featuredEventIndex];
  const featuredIsLive = featuredEvent?.status === "live";

  return (
    <div className="h-dvh overflow-y-auto hide-scrollbar bg-[#0f0f0f]" data-testid="home-page">
      <Navbar user={user} onLogout={onLogout} />

      {/* Location Search Panel */}
      {showSearchPanel && (
        <div className="fixed top-32 right-4 z-50 bg-gray-900/95 backdrop-blur-md border border-gray-700 rounded-lg p-4 w-80 shadow-xl" data-testid="location-search-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Search className="w-4 h-4 text-purple-400" />
              Find Events Near You
            </h3>
            <button onClick={() => setShowSearchPanel(false)} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">City</label>
              <Input
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                placeholder="Enter city name"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="search-city-input"
              />
            </div>
            
            <div>
              <label className="text-gray-400 text-xs mb-1 block">State</label>
              <select
                value={searchState}
                onChange={(e) => setSearchState(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                data-testid="search-state-select"
              >
                <option value="">All States</option>
                {US_STATES.map(state => (
                  <option key={state.code} value={state.code}>{state.name}</option>
                ))}
              </select>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                onClick={searchByLocation}
                disabled={isSearching}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="search-events-button"
              >
                {isSearching ? "Searching..." : "Search"}
              </Button>
              {(searchCity || searchState || filteredEvents.length > 0) && (
                <Button
                  onClick={clearSearch}
                  variant="outline"
                  className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-800"
                  data-testid="clear-search-button"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Active Search Indicator */}
      {filteredEvents.length > 0 && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-40 bg-purple-600/90 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm flex items-center gap-2 shadow-lg" data-testid="search-results-indicator">
          <MapPin className="w-4 h-4" />
          Showing {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} 
          {searchCity && ` in ${searchCity}`}
          {searchState && `, ${searchState}`}
          <button onClick={clearSearch} className="ml-2 hover:bg-purple-700 rounded-full p-1">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      
      {/* Hero Section */}
      {featuredEvent && (
        <div className="relative h-[85vh] w-full" data-testid="hero-section">
          <div className="absolute inset-0 transition-opacity duration-1000">
            {featuredIsLive ? (
              <LiveEventPreview
                key={`${featuredEvent.id}:${featuredEvent.started_at || featuredEvent.stream_ready_at || "live"}`}
                eventId={featuredEvent.id}
                startedAt={featuredEvent.started_at || featuredEvent.stream_ready_at}
                imageUrl={featuredEvent.image_url}
                title={featuredEvent.title}
                viewerIdentity={user?.id}
                variant="hero"
                onPreviewActiveChange={handleFeaturedPreviewActiveChange}
              />
            ) : (
              <img
                src={featuredEvent.image_url}
                alt={featuredEvent.title}
                className="w-full h-full object-cover"
              />
            )}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black via-black/70 via-40% to-black/30 to-70%"></div>
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#0f0f0f] via-[#0f0f0f]/80 via-40% to-transparent"></div>
            {featuredIsLive && (
              <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_110px_rgba(239,68,68,.34)]" />
            )}
          </div>
          
          <div className="relative h-full flex items-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="max-w-2xl animate-fadeIn">
              {featuredIsLive && (
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-red-300/40 bg-red-600 px-3 py-1.5 text-xs font-black uppercase tracking-[0.14em] text-white shadow-[0_0_24px_rgba(239,68,68,.8)]">
                  <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                  Live Now
                </div>
              )}
              <h1 className="text-white text-5xl md:text-7xl font-black mb-4" data-testid="featured-event-title">
                {featuredEvent.title}
              </h1>
              <p className="text-gray-300 text-lg md:text-xl mb-6 line-clamp-3" data-testid="featured-event-description">
                {featuredEvent.description}
              </p>
              <div className="flex items-center gap-4">
                <Button 
                  onClick={() => navigate(`/event/${featuredEvent.id}`)}
                  className={featuredIsLive
                    ? "bg-red-600 hover:bg-red-500 text-white font-bold text-lg px-8 py-6 shadow-[0_0_32px_rgba(239,68,68,.85)]"
                    : "bg-white hover:bg-gray-200 text-black font-bold text-lg px-8 py-6"}
                  data-testid="featured-event-play-button"
                >
                  <Play className="mr-2 h-6 w-6 fill-current" />
                  {featuredIsLive ? "Watch Live Now" : "Buy Tickets"}
                </Button>
                <Button 
                  onClick={() => navigate(`/event/${featuredEvent.id}`)}
                  className="bg-gray-600/80 hover:bg-gray-600 text-white font-bold text-lg px-8 py-6"
                  data-testid="featured-event-info-button"
                >
                  <Info className="mr-2 h-5 w-5" />
                  More Info
                </Button>
              </div>
            </div>
          </div>

          {/* Carousel Indicators */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
            {displayEvents.slice(0, 10).map((_, index) => (
              <button
                key={index}
                onClick={() => setFeaturedEventIndex(index)}
                className={`h-1 transition-all duration-300 ${
                  index === featuredEventIndex
                    ? "w-8 bg-blue-500"
                    : "w-6 bg-gray-500 hover:bg-gray-400"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Search + Event Rows */}
      {/* When no hero is showing, add top padding so the search bar clears the fixed navbar */}
      <div className={`relative pb-20 ${featuredEvent ? "pt-8" : "pt-24"}`}>
        {/* Keyword Search Bar */}
        <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto mb-10" data-testid="home-search-bar">
          <div className="flex items-center gap-2 bg-[#12151d]/95 backdrop-blur-md border border-white/10 rounded-2xl p-2 shadow-2xl">
            <div className="flex items-center gap-2 flex-1 px-3 min-w-0">
              <Search className="w-5 h-5 text-gray-400 shrink-0" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search concerts, comedy, sports, creators…"
                className="flex-1 bg-transparent border-none outline-none text-white text-sm placeholder-gray-500 py-2.5 min-w-0"
                data-testid="home-search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="text-gray-500 hover:text-white shrink-0" data-testid="home-search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          {searchActive && (
            <p className="text-gray-400 text-sm mt-3 px-1" data-testid="home-search-count">
              {displayEvents.length} result{displayEvents.length !== 1 ? "s" : ""}
              {query ? ` for “${searchQuery}”` : ""}
              {(searchCity || searchState) ? ` near ${searchCity}${searchCity && searchState ? ", " : ""}${searchState}` : ""}
            </p>
          )}
        </div>

        {searchActive ? (
          <>
            {/* Unified search results (keyword + location) */}
            {displayEvents.length > 0 ? (
              <EventRow title="Search Results" events={displayEvents.slice(0, 20)} />
            ) : (
              <div className="text-center py-16" data-testid="home-no-results">
                <Search className="w-14 h-14 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No events match your search.</p>
                <Button onClick={clearSearch} className="mt-4 bg-blue-600 hover:bg-blue-700">
                  View All Events
                </Button>
              </div>
            )}
          </>
        ) : events.length > 0 ? (
          <>
            {/* Show all events */}
            <div className="mt-16">
              <EventRow title="Upcoming Events" events={events.slice(0, 10)} />
            </div>
            <div className="mt-16">
              {categories.map((category) => {
                const categoryEvents = getEventsByCategory(category);
                return categoryEvents.length > 0 ? (
                  <EventRow key={category} title={category} events={categoryEvents} />
                ) : null;
              })}
            </div>
          </>
        ) : (
          /* No events loaded yet — graceful placeholder instead of a blank page */
          <div className="text-center py-24 px-4" data-testid="home-empty-state">
            <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-blue-600/15 border border-blue-500/30 flex items-center justify-center">
              <Play className="w-6 h-6 text-blue-400 fill-current" />
            </div>
            <h2 className="text-white text-2xl font-bold mb-2">No live events right now</h2>
            <p className="text-gray-400 text-base max-w-md mx-auto">
              Check back soon — new Religion, Podcast, Education, Influencer, Comedy, and Music events are added all the time.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
